import {
    Card,
    CardBody,
    Typography,
    Button,
    Input,
    Dialog,
    IconButton,
    DialogBody,
    DialogHeader,
    DialogFooter,
} from "@material-tailwind/react";
import {
    ArrowLongUpIcon,
    ArrowLongDownIcon,
    ArrowPathIcon,
    CheckCircleIcon,
    XMarkIcon,
    PencilSquareIcon,
    PlusCircleIcon,
    AdjustmentsHorizontalIcon,
    ArrowTopRightOnSquareIcon,
    TrashIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
    NoSymbolIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useMemo, useRef, useState } from "react";
import getData from "../../api/axiosInstance";
import axios from "axios";
import { useTheme } from "../common/ThemeContext";

const PLATFORMS = ["PS1", "PS2", "PS3", "PS4", "PS5"];
const STATUSES = ["IN_STOCK", "OUT_OF_STOCK"];
const MATCH_STATUSES = [
    "AUTO_CONFIRMED",
    "MANUALLY_CONFIRMED",
    "NEEDS_REVIEW",
    "UNMATCHED",
    "NO_MATCH",
    "EXCLUDED",
    "SOURCE_CONFLICT",
];

const MATCH_STATUS_LABELS = {
    AUTO_CONFIRMED: "Auto confirmed",
    MANUALLY_CONFIRMED: "Manually confirmed",
    NEEDS_REVIEW: "Needs review",
    UNMATCHED: "Unmatched",
    NO_MATCH: "No match",
    EXCLUDED: "Excluded",
    SOURCE_CONFLICT: "Source conflict",
};

const EMPTY_FORM = {
    title: '',
    platform: '',
    ean: '',
    quantity: 1,
    myPrice: 0,
    wobPrice: 0,
    marketPrice: 0,
    wobUrl: '',
    marketUrl: '',
    status: 'OUT_OF_STOCK',
};

export default function WatchlistDashboard() {

    const SYNC_COOLDOWN_MS = 2 * 60 * 60 * 1000;

    const [watchList, setWatchList] = useState([]);
    const [sortSettings, setSortSettings] = useState({ key: 'myPrice', direction: 'desc' });
    const [searchTerm, setSearchTerm] = useState('');
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [itemToEdit, setItemToEdit] = useState({});
    const [deleteTimer, setDeleteTimer] = useState(null);
    const [newItemData, setNewItemData] = useState({ ...EMPTY_FORM });
    const [isLoading, setIsLoading] = useState(true);

    // pagination state
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const hasPages = totalPages > 0;
    const displayPage = hasPages ? currentPage + 1 : 0;
    const isFirstPageDisabled = currentPage <= 0;
    const isLastPageDisabled = !hasPages || currentPage >= totalPages - 1;

    // filter state
    const [hideOutOfStock, setHideOutOfStock] = useState(true);
    const [myMin, setMyMin] = useState('');
    const [myMax, setMyMax] = useState('');
    const [wobMin, setWobMin] = useState('');
    const [wobMax, setWobMax] = useState('');
    const [selectedPlatforms, setSelectedPlatforms] = useState([]);
    const [qtyMin, setQtyMin] = useState('');
    const [qtyMax, setQtyMax] = useState('');
    const [mktMin, setMktMin] = useState('');
    const [mktMax, setMktMax] = useState('');
    const [missingMarketUrl, setMissingMarketUrl] = useState(false);
    const [isChecked, setIsChecked] = useState(null);
    const [selectedMatchStatuses, setSelectedMatchStatuses] = useState([]);
    const [missingEan, setMissingEan] = useState(false);
    const [watchlistSummary, setWatchlistSummary] = useState(null);
    const [syncRuns, setSyncRuns] = useState([]);
    const [isMatchDialogOpen, setIsMatchDialogOpen] = useState(false);
    const [matchDialogItem, setMatchDialogItem] = useState(null);
    const [matchDetails, setMatchDetails] = useState(null);
    const [matchHistory, setMatchHistory] = useState(null);
    const [selectedCandidateId, setSelectedCandidateId] = useState(null);
    const [catalogSearchTerm, setCatalogSearchTerm] = useState('');
    const [catalogSearchResults, setCatalogSearchResults] = useState([]);
    const [catalogSearchTotal, setCatalogSearchTotal] = useState(0);
    const [catalogSearchLoading, setCatalogSearchLoading] = useState(false);
    const [catalogSearchError, setCatalogSearchError] = useState('');
    const [exclusionReason, setExclusionReason] = useState('');
    const [matchActionError, setMatchActionError] = useState('');
    const [matchActionLoading, setMatchActionLoading] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);
    const filterRef = useRef(null);
    const mobileFilterRef = useRef(null);
    const [mobileSortOpen, setMobileSortOpen] = useState(false);
    const mobileSortRef = useRef(null);
    const [editItemId, setEditItemId] = useState(null);

    const getSyncCooldownRemaining = () => {
        const last = localStorage.getItem('lastWatchlistSync');
        if (!last) return 0;
        const remaining = SYNC_COOLDOWN_MS - (Date.now() - parseInt(last));
        return remaining > 0 ? remaining : 0;
    };

    const [syncCooldownRemaining, setSyncCooldownRemaining] = useState(getSyncCooldownRemaining());

    const { isDark } = useTheme();
    const timerRef = useRef(null);
    const token = localStorage.getItem("token");
    const requestHeaders = useMemo(() => ({ 'Authorization': `Bearer ${token}` }), [token]);
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";

    const refreshOverview = async () => {
        try {
            const [summaryResponse, runsResponse] = await Promise.all([
                axios.get(`${API_URL}/watchlist/summary`, { headers: requestHeaders }),
                axios.get(`${API_URL}/watchlist/sync-runs`, { headers: requestHeaders }),
            ]);
            setWatchlistSummary(summaryResponse.data);
            setSyncRuns(runsResponse.data || []);
        } catch (error) {
            console.error('Failed to load Watchlist overview:', error);
        }
    };

    useEffect(() => {
        refreshOverview();
        // Overview is refreshed explicitly after mutations; auth header changes re-load it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestHeaders]);

    useEffect(() => {
        const query = catalogSearchTerm.trim();
        if (!isMatchDialogOpen || !matchDialogItem?.platform || query.length < 2) {
            setCatalogSearchResults([]);
            setCatalogSearchTotal(0);
            setCatalogSearchLoading(false);
            setCatalogSearchError('');
            return undefined;
        }

        const controller = new AbortController();
        const timer = setTimeout(async () => {
            setCatalogSearchLoading(true);
            setCatalogSearchError('');
            try {
                const response = await axios.get(
                    `${API_URL}/watchlist/pricecharting-products/search`,
                    {
                        headers: requestHeaders,
                        params: {
                            platform: matchDialogItem.platform,
                            query,
                            page: 0,
                            size: 20,
                        },
                        signal: controller.signal,
                    },
                );
                setCatalogSearchResults(response.data?.content || []);
                setCatalogSearchTotal(response.data?.totalElements || 0);
            } catch (error) {
                if (error.code !== 'ERR_CANCELED' && error.name !== 'CanceledError') {
                    setCatalogSearchResults([]);
                    setCatalogSearchTotal(0);
                    setCatalogSearchError(
                        error.response?.data?.error || 'Unable to search the local PAL catalog.'
                    );
                }
            } finally {
                if (!controller.signal.aborted) {
                    setCatalogSearchLoading(false);
                }
            }
        }, 250);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [
        API_URL,
        catalogSearchTerm,
        isMatchDialogOpen,
        matchDialogItem?.platform,
        requestHeaders,
    ]);

    useEffect(() => {
        if (syncCooldownRemaining <= 0) return;
        const interval = setInterval(() => {
            const remaining = getSyncCooldownRemaining();
            setSyncCooldownRemaining(remaining);
            if (remaining <= 0) clearInterval(interval);
        }, 1000);
        return () => clearInterval(interval);
    }, [syncCooldownRemaining]);

    const formatCooldown = (ms) => {
        const totalSeconds = Math.ceil(ms / 1000);
        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = totalSeconds % 60;
        if (h > 0) return `${h}h ${m}m`;
        if (m > 0) return `${m}m ${s}s`;
        return `${s}s`;
    };

    const handleWatchlistSync = async () => {
        if (syncCooldownRemaining > 0) return;
        try {
            await axios.post(`${API_URL}/watchlist/sync`, {}, { headers: requestHeaders });
            localStorage.setItem('lastWatchlistSync', Date.now().toString());
            setSyncCooldownRemaining(SYNC_COOLDOWN_MS);
        } catch (err) {
            console.error('Sync failed:', err);
        }
    };

    // ─── Data fetching ────────────────────────────────────────────────────────
    useEffect(() => {
        setIsLoading(true);

        const params = new URLSearchParams({
            page: currentPage.toString(),
            size: itemsPerPage.toString(),
            sortField: sortSettings.key,
            sortDirection: sortSettings.direction,
            ...(searchTerm && { searchTerm }),
            ...(hideOutOfStock && { status: 'IN_STOCK' }),
            ...(selectedPlatforms.length > 0 && { platforms: selectedPlatforms.join(',') }),
            ...(myMin && { myMin }),
            ...(myMax && { myMax }),
            ...(wobMin && { wobMin }),
            ...(wobMax && { wobMax }),
            ...(qtyMin && { qtyMin }),
            ...(qtyMax && { qtyMax }),
            ...(mktMin && { mktMin }),
            ...(mktMax && { mktMax }),
            ...(missingMarketUrl && { missingMarketUrl: 'true' }),
            ...(isChecked !== null && { isChecked: isChecked.toString() }),
            ...(selectedMatchStatuses.length > 0 && { matchStatuses: selectedMatchStatuses.join(',') }),
            ...(missingEan && { missingEan: 'true' }),
        });

        getData(
            `/watchlist/all?${params.toString()}`,
            requestHeaders,
            (data) => {
                setWatchList(data.content);
                setTotalPages(data.totalPages);
                setTotalElements(data.totalElements);
                setIsLoading(false);
            },
            "Error fetching watchlist data"
        );
    }, [requestHeaders, currentPage, itemsPerPage, searchTerm, sortSettings, hideOutOfStock, selectedPlatforms,
        myMin, myMax, wobMin, wobMax, qtyMin, qtyMax, mktMin, mktMax, missingMarketUrl, isChecked,
        selectedMatchStatuses, missingEan]);

    const refreshList = async () => {
        const params = new URLSearchParams({
            page: currentPage.toString(),
            size: itemsPerPage.toString(),
            sortField: sortSettings.key,
            sortDirection: sortSettings.direction,
            ...(searchTerm && { searchTerm }),
            ...(hideOutOfStock && { status: 'IN_STOCK' }),
            ...(selectedPlatforms.length > 0 && { platforms: selectedPlatforms.join(',') }),
            ...(myMin && { myMin }),
            ...(myMax && { myMax }),
            ...(wobMin && { wobMin }),
            ...(wobMax && { wobMax }),
            ...(qtyMin && { qtyMin }),
            ...(qtyMax && { qtyMax }),
            ...(mktMin && { mktMin }),
            ...(mktMax && { mktMax }),
            ...(missingMarketUrl && { missingMarketUrl: 'true' }),
            ...(isChecked !== null && { isChecked: isChecked.toString() }),
            ...(selectedMatchStatuses.length > 0 && { matchStatuses: selectedMatchStatuses.join(',') }),
            ...(missingEan && { missingEan: 'true' }),
        });
        const res = await axios.get(`${API_URL}/watchlist/all?${params.toString()}`, { headers: requestHeaders });
        setWatchList(res.data.content);
        setTotalPages(res.data.totalPages);
        setTotalElements(res.data.totalElements);
        await refreshOverview();
    };

    // useEffect(() => {
    //     if (isEditDialogOpen && editItemId) {
    //         axios.get(`${API_URL}/watchlist/${editItemId}`, { headers: requestHeaders })
    //             .then(res => setItemToEdit(res.data))
    //             .catch(err => console.error("Error fetching item:", err));
    //     }
    // }, [isEditDialogOpen, editItemId, requestHeaders]);

    // Client-side range filters (applied after server fetches)


    // ─── Pagination handlers ──────────────────────────────────────────────────
    const handleFirstPage = () => {
        if (!hasPages) return;
        setCurrentPage(0);
    };
    const handlePrevPage = () => {
        if (!hasPages) return;
        setCurrentPage(prev => Math.max(0, prev - 1));
    };
    const handleNextPage = () => {
        if (!hasPages) return;
        setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
    };
    const handleLastPage = () => {
        if (!hasPages) return;
        setCurrentPage(totalPages - 1);
    };

    const handleSort = (field) => {
        setSortSettings(prev => ({
            key: field,
            direction: prev.key === field && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
        setCurrentPage(0);
    };

    // ─── Handlers ─────────────────────────────────────────────────────────────
    const handleNewInput = (e) => {
        const { name, value } = e.target;
        setNewItemData(prev => ({ ...prev, [name]: value }));
    };

    const handleEditInput = (e) => {
        const { name, value } = e.target;
        setItemToEdit(prev => ({ ...prev, [name]: value }));
    };

    const toggleAddDialog = () => {
        setNewItemData({ ...EMPTY_FORM });
        setIsAddDialogOpen(prev => !prev);
    };

    const toggleEditDialog = (item) => {
        setEditItemId(item?.id || null);
        setItemToEdit(item || {});
        setDeleteTimer(null);
        clearInterval(timerRef.current);
        setIsEditDialogOpen(prev => !prev);
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/watchlist/add`, newItemData, { headers: requestHeaders });
            setCurrentPage(0);
            await refreshList();
            setIsAddDialogOpen(false);
            setNewItemData({ ...EMPTY_FORM });
        } catch (err) { console.error("Error adding:", err); }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            await axios.put(`${API_URL}/watchlist/edit/${itemToEdit.id}`, itemToEdit, { headers: requestHeaders });
            await refreshList();
            setIsEditDialogOpen(false);
        } catch (err) { console.error("Error updating:", err); }
    };

    const handleToggleChecked = async (item) => {
        const nextChecked = item.isChecked === null || item.isChecked === undefined
            ? true
            : item.isChecked === true
                ? false
                : true;
        const previousChecked = item.isChecked;

        setWatchList(prev => prev.map(current =>
            current.id === item.id
                ? { ...current, isChecked: nextChecked }
                : current
        ));

        if (itemToEdit?.id === item.id) {
            setItemToEdit(prev => ({ ...prev, isChecked: nextChecked }));
        }

        try {
            await axios.put(
                `${API_URL}/watchlist/edit/${item.id}`,
                { ...item, isChecked: nextChecked },
                { headers: requestHeaders }
            );
        } catch (err) {
            console.error("Error updating checked status:", err);
            setWatchList(prev => prev.map(current =>
                current.id === item.id
                    ? { ...current, isChecked: previousChecked }
                    : current
            ));
            if (itemToEdit?.id === item.id) {
                setItemToEdit(prev => ({ ...prev, isChecked: previousChecked }));
            }
        }
    };

    const openMatchDialog = async (item) => {
        setMatchDialogItem(item);
        setMatchDetails(null);
        setMatchHistory(null);
        setSelectedCandidateId(item.marketProductId || null);
        setCatalogSearchTerm('');
        setCatalogSearchResults([]);
        setCatalogSearchTotal(0);
        setCatalogSearchError('');
        setExclusionReason(item.matchStatus === 'EXCLUDED' ? (item.matchReason || '') : '');
        setMatchActionError('');
        setIsMatchDialogOpen(true);
        try {
            const [detailsResponse, historyResponse] = await Promise.all([
                axios.get(`${API_URL}/watchlist/${item.id}/match`, { headers: requestHeaders }),
                axios.get(`${API_URL}/watchlist/${item.id}/history`, { headers: requestHeaders }),
            ]);
            const details = detailsResponse.data;
            setMatchDetails(details);
            setMatchHistory(historyResponse.data);
            setSelectedCandidateId(details.selectedPriceChartingProductId || null);
        } catch (error) {
            setMatchActionError(error.response?.data?.error || 'Unable to load match details.');
        }
    };

    const closeMatchDialog = () => {
        setIsMatchDialogOpen(false);
        setMatchDialogItem(null);
        setMatchDetails(null);
        setMatchHistory(null);
        setSelectedCandidateId(null);
        setCatalogSearchTerm('');
        setCatalogSearchResults([]);
        setCatalogSearchTotal(0);
        setCatalogSearchLoading(false);
        setCatalogSearchError('');
        setMatchActionError('');
    };

    const confirmMatch = async () => {
        if (!matchDialogItem || !selectedCandidateId) {
            setMatchActionError('Select a PriceCharting candidate first.');
            return;
        }
        setMatchActionLoading(true);
        setMatchActionError('');
        try {
            await axios.put(
                `${API_URL}/watchlist/${matchDialogItem.id}/match/confirm`,
                { priceChartingProductId: selectedCandidateId, note: 'Confirmed in Arcado Watchlist' },
                { headers: requestHeaders },
            );
            await refreshList();
            closeMatchDialog();
        } catch (error) {
            setMatchActionError(error.response?.data?.error || 'Unable to confirm this match.');
        } finally {
            setMatchActionLoading(false);
        }
    };

    const excludeMatch = async () => {
        if (!matchDialogItem) return;
        const reason = exclusionReason.trim() || 'Manually excluded in Arcado Watchlist';
        setMatchActionLoading(true);
        setMatchActionError('');
        try {
            await axios.put(
                `${API_URL}/watchlist/${matchDialogItem.id}/match/exclude`,
                { reason },
                { headers: requestHeaders },
            );
            await refreshList();
            closeMatchDialog();
        } catch (error) {
            setMatchActionError(error.response?.data?.error || 'Unable to exclude this item.');
        } finally {
            setMatchActionLoading(false);
        }
    };

    const resetMatch = async () => {
        if (!matchDialogItem) return;
        setMatchActionLoading(true);
        setMatchActionError('');
        try {
            await axios.delete(
                `${API_URL}/watchlist/${matchDialogItem.id}/match`,
                { headers: requestHeaders },
            );
            await refreshList();
            closeMatchDialog();
        } catch (error) {
            setMatchActionError(error.response?.data?.error || 'Unable to reset this match.');
        } finally {
            setMatchActionLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!itemToEdit?.id) return;
        try {
            await axios.delete(`${API_URL}/watchlist/delete/${itemToEdit.id}`, { headers: requestHeaders });
            await refreshList();
            setIsEditDialogOpen(false);
            setDeleteTimer(null);
        } catch (err) { console.error("Error deleting:", err); }
    };

    const startDeleteCountdown = () => {
        if (deleteTimer !== null) {
            clearInterval(timerRef.current);
            setDeleteTimer(null);
            return;
        }
        let timeLeft = 5;
        setDeleteTimer(timeLeft);
        timerRef.current = setInterval(() => {
            timeLeft -= 1;
            setDeleteTimer(timeLeft);
            if (timeLeft === 0) { clearInterval(timerRef.current); handleDelete(); }
        }, 1000);
    };

    // ─── Click outside filter ─────────────────────────────────────────────────
    useEffect(() => {
        const handleClick = (e) => {
            if (
                filterRef.current &&
                mobileFilterRef.current &&
                !filterRef.current.contains(e.target) &&
                !mobileFilterRef.current.contains(e.target)
            ) {
                setFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    useEffect(() => {
        const handleClick = (e) => {
            if (mobileSortRef.current && !mobileSortRef.current.contains(e.target)) {
                setMobileSortOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // ─── Helpers ──────────────────────────────────────────────────────────────
    const fmtRSD = (v) => Number.isFinite(v)
        ? v.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '/';
    const fmtGBP = (v) => Number.isFinite(v)
        ? v.toLocaleString('en-US', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '/';

    const fmtRSDCompact = (v) => Number.isFinite(v)
        ? v.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 0, maximumFractionDigits: 0 })
        : '/';
    const fmtGBPCompact = (v) => Number.isFinite(v)
        ? v.toLocaleString('en-US', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '/';
    const fmtUSD = (v) => Number.isFinite(v)
        ? v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '/';
    const fmtUSDCompact = fmtUSD;
    const salesPeriodSuffix = {
        DAY: 'd',
        WEEK: 'w',
        MONTH: 'm',
        YEAR: 'y',
    };
    const fmtSalesVolume = (item) => {
        const suffix = salesPeriodSuffix[item.marketSalesPeriod];
        if (Number.isFinite(item.marketSalesCount) && suffix) {
            const count = item.marketSalesCount.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            });
            return `${count}/${suffix}`;
        }
        return item.marketSalesVolumeText || '/';
    };
    const salesVolumeTitle = (item) => {
        if (!item.marketSalesVolumeText) return 'CIB sales volume has not been checked yet';
        const checked = item.marketSalesVolumeCheckedAt
            ? ` · checked ${new Date(item.marketSalesVolumeCheckedAt).toLocaleString()}`
            : '';
        return `${item.marketSalesVolumeText}${checked}`;
    };

    const SortIcon = ({ colKey }) => sortSettings?.key === colKey
        ? sortSettings.direction === 'desc'
            ? <ArrowLongUpIcon strokeWidth={2} className="w-3 h-3 inline ml-0.5 text-cyan-700 dark:text-cyan-300" />
            : <ArrowLongDownIcon strokeWidth={2} className="w-3 h-3 inline ml-0.5 text-cyan-700 dark:text-cyan-300" />
        : null;

    // ─── Reusable field components ────────────────────────────────────────────
    const inputCls = "placeholder:opacity-100 focus:!border-t-gray-900";
    const labelHidden = { className: "hidden" };
    const containerFull = { className: "!min-w-full" };
    const readOnlyCls = `${inputCls} !bg-gray-50 dark:!bg-dark-800 cursor-default`;

    const FieldLabel = ({ children }) => (
        <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold dark:text-gray-300">
            {children}
        </Typography>
    );

    const DialogFields = ({ data, onChange, isNew = false }) => (
        <div className="space-y-4">
            <div>
                <FieldLabel>Title</FieldLabel>
                <Input color="gray" size="lg"
                       name="title" value={data.title || ''} readOnly={!isNew} onChange={onChange}
                       className={isNew ? inputCls : readOnlyCls}
                       containerProps={containerFull} labelProps={labelHidden} />
            </div>
            <div className="flex gap-3">
                <div className="w-full">
                    <FieldLabel>Platform</FieldLabel>
                    <select
                        name="platform" value={data.platform || ''} disabled={!isNew} onChange={onChange}
                        className={`w-full rounded-lg border border-blue-gray-200 px-3 py-2.5 text-sm ${
                            isNew
                                ? 'bg-white text-blue-gray-700 dark:border-gray-700 dark:bg-dark-800 dark:text-gray-200'
                                : 'bg-gray-50 text-blue-gray-700 dark:border-gray-700 dark:bg-dark-800 dark:text-gray-400 cursor-default opacity-70'
                        }`}
                    >
                        <option value="">—</option>
                        {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>
                <div className="w-[120px] shrink-0">
                    <FieldLabel>Quantity</FieldLabel>
                    <Input type="number" color="gray" size="lg"
                           name="quantity" value={data.quantity ?? 1} readOnly
                           className={readOnlyCls} containerProps={containerFull} labelProps={labelHidden} />
                </div>
            </div>
            <div>
                <FieldLabel>EAN</FieldLabel>
                <Input color="gray" size="lg" placeholder="12–14 digits"
                       name="ean" value={data.ean || ''} readOnly={!isNew} onChange={onChange}
                       className={isNew ? inputCls : readOnlyCls}
                       containerProps={containerFull} labelProps={labelHidden} />
            </div>
            <div className="flex gap-3">
                <div className="w-full">
                    <FieldLabel>MY Price (RSD)</FieldLabel>
                    <Input type="number" step="0.01" color="gray" size="lg" placeholder="0.00"
                           name="myPrice" value={data.myPrice ?? 0} readOnly
                           className={readOnlyCls} containerProps={containerFull} labelProps={labelHidden} />
                </div>
                <div className="w-full">
                    <FieldLabel>WOB Price (GBP)</FieldLabel>
                    <Input type="number" step="0.01" color="gray" size="lg"
                           name="wobPrice" value={data.wobPrice ?? 0} readOnly
                           className={readOnlyCls} containerProps={containerFull} labelProps={labelHidden} />
                </div>
                <div className="w-full">
                    <FieldLabel>Market CIB (USD)</FieldLabel>
                    <Input type="number" step="0.01" color="gray" size="lg"
                           name="marketPrice" value={data.marketPrice ?? 0} readOnly
                           className={readOnlyCls} containerProps={containerFull} labelProps={labelHidden} />
                </div>
            </div>
            <div className="flex gap-3">
                <div className="w-full">
                    <FieldLabel>Wob URL</FieldLabel>
                    <Input color="gray" size="lg" placeholder="https://..."
                           name="wobUrl" value={data.wobUrl || ''} onChange={onChange}
                           className={inputCls} containerProps={containerFull} labelProps={labelHidden} />
                </div>
                <div className="w-full">
                    <FieldLabel>Market URL</FieldLabel>
                    <Input color="gray" size="lg" placeholder="https://..."
                           name="marketUrl" value={data.marketUrl || ''} readOnly
                           className={readOnlyCls} containerProps={containerFull} labelProps={labelHidden} />
                </div>
            </div>

            <div className="flex gap-3">
                <div className="w-full">
                    <FieldLabel>Status</FieldLabel>
                    <select
                        name="status" value={data.status || ''} disabled
                        className="w-full rounded-lg border border-blue-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-blue-gray-700 dark:border-gray-700 dark:bg-dark-800 dark:text-gray-400 cursor-default opacity-70"
                    >
                        {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                    </select>
                </div>
                <div className="w-full">
                    <FieldLabel>Checked</FieldLabel>
                    <select
                        name="isChecked"
                        value={data.isChecked === null || data.isChecked === undefined ? '' : data.isChecked.toString()}
                        onChange={(e) => onChange({ target: { name: 'isChecked', value: e.target.value === '' ? null : e.target.value === 'true' } })}
                        className="w-full rounded-lg border border-blue-gray-200 bg-white px-3 py-2.5 text-sm text-blue-gray-700 dark:border-gray-700 dark:bg-dark-800 dark:text-gray-200"
                    >
                        <option value="">—</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                </div>
            </div>
        </div>
    );

    const activeFilterCount = (hideOutOfStock ? 1 : 0) +
        (selectedPlatforms.length > 0 ? 1 : 0) +
        (myMin || myMax ? 1 : 0) +
        (wobMin || wobMax ? 1 : 0) +
        (qtyMin || qtyMax ? 1 : 0) +
        (mktMin || mktMax ? 1 : 0) +
        (isChecked !== null ? 1 : 0) +
        (missingMarketUrl ? 1 : 0) +
        (selectedMatchStatuses.length > 0 ? 1 : 0) +
        (missingEan ? 1 : 0);

    const sortOptions = [
        { key: 'title', label: 'Title' },
        { key: 'platform', label: 'Platform' },
        { key: 'quantity', label: 'Quantity' },
        { key: 'myPrice', label: 'MY Price' },
        { key: 'wobPrice', label: 'WOB Price' },
        { key: 'marketPrice', label: 'Market Price' },
        { key: 'marketSalesPerDay', label: 'CIB Sales Volume' },
        { key: 'matchStatus', label: 'Match Status' },
        { key: 'matchConfidence', label: 'Match Confidence' },
        { key: 'status', label: 'Status' },
        { key: 'isChecked', label: 'Checked' },
    ];
    const sortedOptionLabel = sortOptions.find((option) => option.key === sortSettings.key)?.label || 'Sort';

    const statusClass = (status) => [
        "inline-flex items-center rounded px-1.5 py-0.5 text-xs font-semibold",
        status === 'IN_STOCK'
            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
            : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
    ].join(" ");

    const matchStatusClass = (status) => {
        const colors = {
            AUTO_CONFIRMED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
            MANUALLY_CONFIRMED: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300",
            NEEDS_REVIEW: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
            SOURCE_CONFLICT: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
            EXCLUDED: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
            NO_MATCH: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
            UNMATCHED: "bg-gray-100 text-gray-600 dark:bg-dark-700 dark:text-gray-300",
        };
        return `inline-flex items-center rounded px-1.5 py-0.5 text-xs font-semibold ${colors[status] || colors.UNMATCHED}`;
    };

    const MatchStatusBadge = ({ item }) => {
        const status = item.matchStatus || 'UNMATCHED';
        const confidence = Number.isFinite(item.matchConfidence)
            ? ` · ${Math.round(item.matchConfidence * 100)}%`
            : '';
        return (
            <button
                type="button"
                onClick={() => openMatchDialog(item)}
                className={`${matchStatusClass(status)} hover:ring-2 hover:ring-cyan-500/40`}
                title={item.matchReason || 'Open PriceCharting match review'}
            >
                {MATCH_STATUS_LABELS[status] || status}{confidence}
            </button>
        );
    };

    const CatalogProductCard = ({ product, showScore = false }) => {
        const selected = selectedCandidateId === product.priceChartingProductId;
        return (
            <button
                type="button"
                onClick={() => {
                    setSelectedCandidateId(product.priceChartingProductId);
                    setMatchActionError('');
                }}
                className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${
                    selected
                        ? 'border-cyan-600 bg-cyan-50 dark:border-cyan-400 dark:bg-cyan-950/30'
                        : 'border-gray-200 hover:border-cyan-300 dark:border-gray-700 dark:hover:border-cyan-700'
                }`}
            >
                {product.imageUrl ? (
                    <img
                        src={product.imageUrl}
                        alt=""
                        loading="lazy"
                        className="h-14 w-11 shrink-0 rounded object-cover"
                    />
                ) : (
                    <div className="h-14 w-11 shrink-0 rounded bg-gray-100 dark:bg-dark-700" />
                )}
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {product.title}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        PAL {product.platform} · PC ID {product.priceChartingProductId}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {showScore && Number.isFinite(product.score)
                            ? `Score ${Math.round(product.score * 100)}% · `
                            : ''}
                        CIB {fmtUSD(product.cibPriceUsd)}
                    </p>
                </div>
                {selected && <CheckCircleIcon className="h-5 w-5 shrink-0 text-cyan-700 dark:text-cyan-300" />}
                <a
                    href={product.url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    className="shrink-0 text-cyan-700 dark:text-cyan-300"
                    title="Open PriceCharting"
                >
                    <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                </a>
            </button>
        );
    };

    const CheckedToggle = ({ item, className = "", showLabel = false }) => {
        const isYes = item.isChecked === true;
        const isNo = item.isChecked === false;
        const label = isYes ? 'Checked: yes' : isNo ? 'Checked: no' : 'Checked: not set';

        return (
            <button
                type="button"
                onClick={() => handleToggleChecked(item)}
                aria-label={label}
                title={`${label}. Click to toggle Yes or No.`}
                className={`inline-flex h-5 shrink-0 items-center justify-center gap-1 rounded border px-1.5 text-[10px] font-bold leading-none transition-colors ${
                    isYes
                        ? 'border-cyan-700 bg-cyan-700 text-white dark:border-cyan-500 dark:bg-cyan-600'
                        : isNo
                            ? 'border-rose-500 bg-rose-50 text-rose-700 dark:border-rose-500 dark:bg-rose-950/40 dark:text-rose-300'
                            : 'border-gray-300 bg-white text-gray-500 hover:border-cyan-600 dark:border-gray-600 dark:bg-dark-800 dark:text-gray-400'
                } ${className}`}
            >
                {showLabel && <span className="font-medium opacity-80">Checked</span>}
                <span className="min-w-5 text-center">{isYes ? 'YES' : isNo ? 'NO' : '--'}</span>
            </button>
        );
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <Card className="w-full shadow-[0_0_15px_rgba(0,0,0,0.1)] relative overflow-hidden dark:bg-dark-900">
            <CardBody className="relative p-3 sm:p-6">

                {/* Header */}
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <Typography variant="h5" color="blue-gray" className="dark:text-gray-300">
                        Watchlist
                    </Typography>
                    <div className="grid grid-cols-[minmax(0,1fr)_72px] gap-2 sm:flex">
                        <button
                            onClick={handleWatchlistSync}
                            disabled={syncCooldownRemaining > 0}
                            className={`flex h-10 items-center justify-center gap-2 rounded-lg px-3 text-sm font-medium transition-colors ${
                                syncCooldownRemaining > 0
                                    ? 'bg-gray-400 text-white cursor-not-allowed'
                                    : 'bg-cyan-800 text-white hover:bg-cyan-700'
                            }`}
                        >
                            <ArrowPathIcon className="h-4 w-4" />
                            <span>
                                {syncCooldownRemaining > 0
                                    ? `Sync Data (${formatCooldown(syncCooldownRemaining)})`
                                    : 'Sync Data'}
                            </span>
                        </button>
                        <Button onClick={toggleAddDialog} size="sm" className="mv-primary-button">
                            <PlusCircleIcon className="h-4 w-4" />
                            Add
                        </Button>
                    </div>
                </div>
                <hr className="my-2 border-blue-gray-50 dark:border-gray-700" />

                {watchlistSummary && (
                    <div className="mt-3 grid grid-cols-2 gap-2 lg:grid-cols-5">
                        {[
                            ['Visible data', watchlistSummary.total],
                            ['In stock', watchlistSummary.inStock],
                            ['Needs review', watchlistSummary.byMatchStatus?.NEEDS_REVIEW || 0],
                            ['Unmatched', (watchlistSummary.byMatchStatus?.UNMATCHED || 0) + (watchlistSummary.byMatchStatus?.NO_MATCH || 0)],
                            ['Missing EAN', watchlistSummary.missingEan],
                        ].map(([label, value]) => (
                            <div key={label} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-dark-800">
                                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</p>
                                <p className="mt-0.5 text-lg font-semibold text-gray-800 dark:text-gray-200">{value ?? 0}</p>
                            </div>
                        ))}
                    </div>
                )}
                {syncRuns[0] && (
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-cyan-50 px-3 py-2 text-xs text-cyan-900 dark:bg-cyan-950/30 dark:text-cyan-200">
                        <span className="font-semibold">Last pipeline run: {syncRuns[0].runType?.replace(/_/g, ' ')}</span>
                        <span>{syncRuns[0].status}</span>
                        <span>{syncRuns[0].processedCount || 0} processed</span>
                        <span>{syncRuns[0].errorCount || 0} errors</span>
                        <span>{syncRuns[0].startedAt ? new Date(syncRuns[0].startedAt).toLocaleString() : ''}</span>
                    </div>
                )}

                <div className="mt-4 mb-3 grid grid-cols-[minmax(0,1fr)_48px_40px] items-center gap-2 md:hidden">
                    <div className="relative min-w-0">
                        <Input type="search" variant="outlined" label="Title" placeholder="Search by title"
                               containerProps={{ className: "min-w-0" }}
                               className="placeholder:text-blue-gray-300 dark:bg-dark-800"
                               color={isDark ? "white" : ""}
                               value={searchTerm}
                               onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }} />
                    </div>

                    <div className="relative" ref={mobileFilterRef}>
                        <button
                            onClick={() => setFilterOpen(p => !p)}
                            className="mv-icon-button w-12 gap-1">
                            <AdjustmentsHorizontalIcon className="w-5 h-5" />
                            <span className="text-xs">({activeFilterCount})</span>
                        </button>

                        {filterOpen && (
                            <div className="absolute top-full mt-2 right-0 z-50 w-[min(300px,calc(100vw-2rem))] bg-white dark:bg-dark-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl p-3">
                                <div className="flex items-center gap-2 mb-3 cursor-pointer select-none" onClick={() => { setHideOutOfStock(p => !p); setCurrentPage(0); }}>
                                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${hideOutOfStock ? 'bg-cyan-700 border-cyan-700' : 'border-gray-400 dark:border-gray-500'}`}>
                                        {hideOutOfStock && <span className="text-white text-[10px] font-bold leading-none">✓</span>}
                                    </div>
                                    <span className="text-sm text-gray-700 dark:text-white">Hide OUT OF STOCK</span>
                                </div>

                                <div className="flex items-center gap-2 mb-3 cursor-pointer select-none" onClick={() => { setMissingMarketUrl(p => !p); setCurrentPage(0); }}>
                                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${missingMarketUrl ? 'bg-cyan-700 border-cyan-700' : 'border-gray-400 dark:border-gray-500'}`}>
                                        {missingMarketUrl && <span className="text-white text-[10px] font-bold leading-none">✓</span>}
                                    </div>
                                    <span className="text-sm text-gray-700 dark:text-white">Missing Market URL</span>
                                </div>

                                <div className="flex items-center gap-2 mb-3 cursor-pointer select-none" onClick={() => { setMissingEan(p => !p); setCurrentPage(0); }}>
                                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${missingEan ? 'bg-cyan-700 border-cyan-700' : 'border-gray-400 dark:border-gray-500'}`}>
                                        {missingEan && <span className="text-white text-[10px] font-bold leading-none">✓</span>}
                                    </div>
                                    <span className="text-sm text-gray-700 dark:text-white">Missing EAN</span>
                                </div>

                                <div className="mb-3">
                                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Match status</span>
                                    <div className="mt-1.5 flex flex-wrap gap-1">
                                        {MATCH_STATUSES.map(status => (
                                            <button
                                                type="button"
                                                key={status}
                                                onClick={() => {
                                                    setSelectedMatchStatuses(previous => previous.includes(status)
                                                        ? previous.filter(value => value !== status)
                                                        : [...previous, status]);
                                                    setCurrentPage(0);
                                                }}
                                                className={`rounded px-2 py-1 text-[10px] font-semibold ${
                                                    selectedMatchStatuses.includes(status)
                                                        ? 'bg-cyan-700 text-white'
                                                        : 'bg-gray-100 text-gray-600 dark:bg-dark-700 dark:text-gray-300'
                                                }`}
                                            >
                                                {MATCH_STATUS_LABELS[status]}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Checked</span>
                                    <select
                                        value={isChecked === null ? '' : isChecked.toString()}
                                        onChange={(e) => { setIsChecked(e.target.value === '' ? null : e.target.value === 'true'); setCurrentPage(0); }}
                                        className="w-full mt-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-900 px-2 py-1 text-xs text-gray-900 dark:text-gray-200 focus:outline-none focus:border-cyan-500"
                                    >
                                        <option value="">All</option>
                                        <option value="true">Checked</option>
                                        <option value="false">Not Checked</option>
                                    </select>
                                </div>

                                <hr className="mb-3 border-gray-200 dark:border-gray-600" />

                                <div className="mb-3">
                                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Platform</span>
                                    <div className="flex gap-1 mt-1.5">
                                        {PLATFORMS.map(p => (
                                            <button key={p}
                                                    onClick={() => { setSelectedPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]); setCurrentPage(0); }}
                                                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${selectedPlatforms.includes(p) ? 'bg-cyan-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-dark-700 dark:text-gray-300'}`}>
                                                {p}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <hr className="mb-3 border-gray-200 dark:border-gray-600" />

                                {[
                                    { label: 'QTY', min: qtyMin, max: qtyMax, setMin: setQtyMin, setMax: setQtyMax, step: '1' },
                                    { label: 'MY (RSD)', min: myMin, max: myMax, setMin: setMyMin, setMax: setMyMax, step: '1' },
                                    { label: 'WOB (GBP)', min: wobMin, max: wobMax, setMin: setWobMin, setMax: setWobMax, step: '0.01' },
                                    { label: 'MKT (USD)', min: mktMin, max: mktMax, setMin: setMktMin, setMax: setMktMax, step: '0.01' },
                                ].map(({ label, min, max, setMin, setMax, step }) => (
                                    <div key={label} className="flex items-center gap-2 mb-2">
                                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-[76px] shrink-0">{label}</span>
                                        <input type="number" step={step} placeholder="min" value={min}
                                               onChange={(e) => setMin(e.target.value)}
                                               className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-900 px-2 py-1 text-xs text-gray-900 dark:text-gray-200 focus:outline-none focus:border-cyan-500" />
                                        <span className="text-gray-300 text-xs shrink-0">—</span>
                                        <input type="number" step={step} placeholder="max" value={max}
                                               onChange={(e) => setMax(e.target.value)}
                                               className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-900 px-2 py-1 text-xs text-gray-900 dark:text-gray-200 focus:outline-none focus:border-cyan-500" />
                                    </div>
                                ))}

                                <hr className="mt-2 mb-2 border-gray-200 dark:border-gray-600" />

                                <button onClick={() => {
                                    setHideOutOfStock(false); setSelectedPlatforms([]);
                                    setMyMin(''); setMyMax(''); setWobMin(''); setWobMax('');
                                    setQtyMin(''); setQtyMax(''); setMktMin(''); setMktMax(''); setMissingMarketUrl(false); setIsChecked(null);
                                    setSelectedMatchStatuses([]); setMissingEan(false);
                                    setCurrentPage(0);
                                }} className="w-full text-center text-xs text-cyan-600 dark:text-cyan-400 hover:text-cyan-800 py-1">
                                    Reset all filters
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="relative" ref={mobileSortRef}>
                        <button
                            onClick={() => setMobileSortOpen(p => !p)}
                            className="mv-icon-button">
                            {sortSettings.direction === 'desc'
                                ? <ArrowLongDownIcon className="w-5 h-5" />
                                : <ArrowLongUpIcon className="w-5 h-5" />}
                        </button>

                        {mobileSortOpen && (
                            <div className="absolute top-full mt-2 right-0 z-50 w-[min(270px,calc(100vw-2rem))] max-h-[60vh] overflow-y-auto bg-white dark:bg-dark-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl p-3">
                                <div className="flex items-center justify-between gap-3">
                                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Sort by</span>
                                    <span className="truncate text-xs text-cyan-800 dark:text-cyan-300">
                                        {sortedOptionLabel} {sortSettings.direction}
                                    </span>
                                </div>
                                <div className="mt-2 space-y-1">
                                    {sortOptions.map((option) => {
                                        const isActive = sortSettings.key === option.key;

                                        return (
                                            <button
                                                key={option.key}
                                                type="button"
                                                onClick={() => handleSort(option.key)}
                                                className={`flex w-full items-center justify-between gap-3 rounded px-2 py-2 text-left text-sm transition-colors ${
                                                    isActive
                                                        ? 'bg-cyan-700 text-white dark:bg-cyan-800'
                                                        : 'text-gray-700 hover:bg-gray-100 dark:text-white dark:hover:bg-dark-700'
                                                }`}
                                            >
                                                <span className="truncate">{option.label}</span>
                                                {isActive && (
                                                    sortSettings.direction === 'desc'
                                                        ? <ArrowLongDownIcon className="h-4 w-4 shrink-0" />
                                                        : <ArrowLongUpIcon className="h-4 w-4 shrink-0" />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className={`relative hidden overflow-x-auto h-[calc(100vh-24rem)] md:block ${isLoading ? 'overflow-x-hidden' : ''} scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 dark:scrollbar-thumb-gray-600 dark:scrollbar-track-dark-800`}>
                    <table className="w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700">
                        <colgroup>
                            <col className="w-[32px]" />
                            <col className="w-[72px]" />
                            <col />
                        </colgroup>
                        <thead className="sticky top-0 bg-gray-50 z-10 dark:bg-dark-800">
                        <tr>
                            <th className="w-[32px]"><span className="invisible">.</span></th>
                            <th className="w-[72px] px-1 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
                                Checked
                            </th>
                            <th className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700">
                                Title
                            </th>
                        </tr>
                        </thead>

                        {isLoading ? (
                            <tbody className="bg-white dark:bg-dark-900 h-[calc(100vh-24rem)]">
                            <tr>
                                <td colSpan={3} className="px-4 py-1.5">
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="flex flex-col items-center justify-center space-y-4">
                                            <div className="relative w-24 h-24">
                                                <img
                                                    src="/images/arcado-logo-solo-circle.png"
                                                    alt="Arcado Logo"
                                                    className="w-full h-full object-contain animate-[pulse_1.5s_ease-in-out_infinite]"
                                                />
                                                <div className="absolute inset-0 rounded-full border-4 border-cyan-900 dark:border-cyan-200 border-opacity-30 animate-[ping_1.5s_ease-in-out_infinite]"></div>
                                            </div>
                                            <div className="text-center">
                                                <Typography variant="h6" className="text-gray-800 dark:text-gray-500 font-bold">
                                                    Loading watchlist...
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            </tbody>
                        ) : (
                            <tbody className="bg-white divide-y dark:bg-dark-900 divide-gray-200 dark:divide-gray-700">
                            {watchList.map((item) => (
                                <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-dark-800/40">
                                    <td className="px-2 py-2 align-middle">
                                        <PencilSquareIcon
                                            onClick={() => toggleEditDialog(item)}
                                            className="w-3.5 h-3.5 text-cyan-900 dark:text-cyan-200 cursor-pointer"
                                        />
                                    </td>
                                    <td className="px-1 py-2 text-center align-middle">
                                        <CheckedToggle item={item} className="mx-auto" />
                                    </td>
                                    <td className="px-3 py-1.5 align-middle">
                                        <div className="flex items-center justify-between gap-3 min-w-0">
                                            {/* LEFT */}
                                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                                <span className={[
                                                    "inline-flex shrink-0 h-2 w-2 rounded-full",
                                                    item.status === 'IN_STOCK' ? "bg-emerald-500" : "bg-rose-500"
                                                ].join(" ")} title={item.status === 'IN_STOCK' ? 'IN STOCK' : 'OUT OF STOCK'} />

                                                <span className="text-sm font-semibold text-gray-900 dark:text-gray-200 truncate">
                                                    {item.title}
                                                </span>

                                                <span className="shrink-0 inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 dark:bg-dark-700 dark:text-gray-300">
                                                    {item.platform}
                                                </span>
                                                <span className="shrink-0 inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 dark:bg-dark-700 dark:text-gray-300">
                                                    ×{Number.isFinite(item.quantity) ? item.quantity : 0}
                                                </span>
                                                <span className={[
                                                    "shrink-0 inline-flex items-center rounded px-1.5 py-0.5 text-xs font-semibold",
                                                    item.status === 'IN_STOCK'
                                                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                                        : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                                                ].join(" ")}>
                                                    {item.status === 'IN_STOCK' ? 'IN STOCK' : 'OUT'}
                                                </span>
                                                <MatchStatusBadge item={item} />
                                                {item.listingCount > 1 && (
                                                    <span
                                                        className="shrink-0 rounded bg-violet-100 px-1.5 py-0.5 text-xs font-semibold text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
                                                        title={`${item.listingCount} WoB URLs are retained for this product`}
                                                    >
                                                        {item.listingCount} W links
                                                    </span>
                                                )}

                                                {item.wobUrl ? (
                                                    <a href={item.wobUrl} target="_blank" rel="noreferrer" title="Wob URL"
                                                       className="shrink-0 inline-flex items-center gap-0.5 text-xs text-cyan-700 hover:text-cyan-900 dark:text-cyan-400 dark:hover:text-cyan-200">
                                                        <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                                                        <span className="font-medium">W</span>
                                                    </a>
                                                ) : (
                                                    <span className="shrink-0 inline-flex items-center gap-0.5 text-xs text-gray-300 dark:text-gray-600" title="No Wob URL">
                                                        <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" /><span>W</span>
                                                    </span>
                                                )}
                                                {item.marketUrl ? (
                                                    <a href={item.marketUrl} target="_blank" rel="noreferrer" title="Market URL"
                                                       className="shrink-0 inline-flex items-center gap-0.5 text-xs text-cyan-700 hover:text-cyan-900 dark:text-cyan-400 dark:hover:text-cyan-200">
                                                        <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                                                        <span className="font-medium">M</span>
                                                    </a>
                                                ) : (
                                                    <span className="shrink-0 inline-flex items-center gap-0.5 text-xs text-gray-300 dark:text-gray-600" title="No Market URL">
                                                        <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" /><span>M</span>
                                                    </span>
                                                )}
                                            </div>

                                            {/* RIGHT: prices */}
                                            <div className="shrink-0 flex items-center gap-3 text-xs">
                                                <div className="flex items-center gap-1">
                                                    <span onClick={() => handleSort('myPrice')}
                                                          className="text-gray-400 dark:text-gray-500 cursor-pointer select-none hover:text-cyan-700 dark:hover:text-cyan-300 uppercase font-medium">
                                                        MY<SortIcon colKey="myPrice" />
                                                    </span>
                                                    <span className="font-semibold text-gray-800 dark:text-gray-200 tabular-nums whitespace-nowrap">
                                                        {fmtRSD(item.myPrice)}
                                                    </span>
                                                </div>
                                                <span className="text-gray-200 dark:text-gray-700">|</span>
                                                <div className="flex items-center gap-1">
                                                    <span onClick={() => handleSort('wobPrice')}
                                                          className="text-gray-400 dark:text-gray-500 cursor-pointer select-none hover:text-cyan-700 dark:hover:text-cyan-300 uppercase font-medium">
                                                        WOB<SortIcon colKey="wobPrice" />
                                                    </span>
                                                    <span className="font-semibold text-gray-800 dark:text-gray-200 tabular-nums whitespace-nowrap">
                                                        {fmtGBP(item.wobPrice)}
                                                    </span>
                                                </div>
                                                <span className="text-gray-200 dark:text-gray-700">|</span>
                                                <div className="flex items-center gap-1">
                                                    <span onClick={() => handleSort('marketPrice')}
                                                          className="text-gray-400 dark:text-gray-500 cursor-pointer select-none hover:text-cyan-700 dark:hover:text-cyan-300 uppercase font-medium">
                                                        MKT<SortIcon colKey="marketPrice" />
                                                    </span>
                                                    <span className="font-semibold text-gray-800 dark:text-gray-200 tabular-nums whitespace-nowrap">
                                                        {fmtUSD(item.marketPrice)}
                                                    </span>
                                                </div>
                                                <span className="text-gray-200 dark:text-gray-700">|</span>
                                                <div className="flex items-center gap-1" title={salesVolumeTitle(item)}>
                                                    <span onClick={() => handleSort('marketSalesPerDay')}
                                                          className="text-gray-400 dark:text-gray-500 cursor-pointer select-none hover:text-cyan-700 dark:hover:text-cyan-300 uppercase font-medium">
                                                        VOL<SortIcon colKey="marketSalesPerDay" />
                                                    </span>
                                                    <span className="font-semibold text-gray-800 dark:text-gray-200 tabular-nums whitespace-nowrap">
                                                        {fmtSalesVolume(item)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        )}
                    </table>
                </div>

                <div className="md:hidden">
                    {isLoading ? (
                        <div className="flex min-h-[44vh] items-center justify-center rounded-lg bg-white dark:bg-dark-900">
                            <div className="flex flex-col items-center justify-center space-y-3">
                                <div className="relative w-20 h-20">
                                    <img
                                        src="/images/arcado-logo-solo-circle.png"
                                        alt="Arcado Logo"
                                        className="w-full h-full object-contain animate-[pulse_1.5s_ease-in-out_infinite]"
                                    />
                                    <div className="absolute inset-0 rounded-full border-4 border-cyan-900 dark:border-cyan-200 border-opacity-30 animate-[ping_1.5s_ease-in-out_infinite]"></div>
                                </div>
                                <Typography variant="small" className="text-gray-600 dark:text-gray-300">
                                    Loading watchlist...
                                </Typography>
                            </div>
                        </div>
                    ) : (
                        <div className="max-h-[calc(100vh-20rem)] overflow-y-auto divide-y divide-gray-200 dark:divide-gray-800 rounded-lg border border-gray-100 dark:border-gray-800 bg-white dark:bg-dark-900">
                            {watchList.map((item) => (
                                <div key={item.id} className="px-3 py-3">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-start gap-2">
                                                <span className={[
                                                    "mt-1.5 inline-flex h-2.5 w-2.5 shrink-0 rounded-full",
                                                    item.status === 'IN_STOCK' ? "bg-emerald-500" : "bg-rose-500"
                                                ].join(" ")} />
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <p className="line-clamp-2 text-sm font-semibold leading-snug text-gray-900 dark:text-gray-200">
                                                            {item.title}
                                                        </p>
                                                        <div className="flex shrink-0 items-center gap-2">
                                                            <CheckedToggle item={item} showLabel className="mt-0.5 h-6" />
                                                            <PencilSquareIcon
                                                                onClick={() => toggleEditDialog(item)}
                                                                className="mt-0.5 h-5 w-5 cursor-pointer text-cyan-900 dark:text-cyan-200"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="mt-1 flex flex-wrap items-center gap-2">
                                                        <span className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-dark-700 dark:text-gray-300">
                                                            {item.platform || '/'}
                                                        </span>
                                                        <span className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-dark-700 dark:text-gray-300">
                                                            x{Number.isFinite(item.quantity) ? item.quantity : 0}
                                                        </span>
                                                        <span className={statusClass(item.status)}>
                                                            {item.status === 'IN_STOCK' ? 'In stock' : 'Out of stock'}
                                                        </span>
                                                        <MatchStatusBadge item={item} />
                                                        {item.listingCount > 1 && (
                                                            <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[11px] font-semibold text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                                                                {item.listingCount} W links
                                                            </span>
                                                        )}
                                                        {item.isChecked === true && (
                                                            <span className="inline-flex items-center rounded bg-cyan-50 px-1.5 py-0.5 text-xs font-medium text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300">
                                                                Checked
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                                                <div>
                                                    <p className="text-gray-400 dark:text-gray-500">MY</p>
                                                    <p className="font-semibold text-gray-900 dark:text-gray-200">{fmtRSDCompact(item.myPrice)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 dark:text-gray-500">WOB</p>
                                                    <p className="font-semibold text-gray-900 dark:text-gray-200">{fmtGBPCompact(item.wobPrice)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 dark:text-gray-500">MKT</p>
                                                    <p className="font-semibold text-gray-900 dark:text-gray-200">{fmtUSDCompact(item.marketPrice)}</p>
                                                </div>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={() => handleSort('marketSalesPerDay')}
                                                title={salesVolumeTitle(item)}
                                                className="mt-2 flex w-full items-center justify-between rounded bg-gray-50 px-2 py-1.5 text-left text-xs dark:bg-dark-800"
                                            >
                                                <span className="font-medium text-gray-400 dark:text-gray-500">
                                                    CIB volume<SortIcon colKey="marketSalesPerDay" />
                                                </span>
                                                <span className="font-semibold text-gray-900 dark:text-gray-200">
                                                    {fmtSalesVolume(item)}
                                                </span>
                                            </button>

                                            <div className="mt-2 flex items-center gap-3">
                                                {item.wobUrl ? (
                                                    <a href={item.wobUrl} target="_blank" rel="noreferrer"
                                                       className="inline-flex items-center gap-1 text-xs font-medium text-cyan-700 dark:text-cyan-400">
                                                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                                        WOB
                                                    </a>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-xs text-gray-300 dark:text-gray-600">
                                                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                                        WOB
                                                    </span>
                                                )}
                                                {item.marketUrl ? (
                                                    <a href={item.marketUrl} target="_blank" rel="noreferrer"
                                                       className="inline-flex items-center gap-1 text-xs font-medium text-cyan-700 dark:text-cyan-400">
                                                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                                        Market
                                                    </a>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-xs text-gray-300 dark:text-gray-600">
                                                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                                        Market
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Pagination — identical to InventoryDashboard */}
                <div className="mt-4 mb-4 flex flex-col gap-3 md:grid md:grid-cols-3 md:items-center">
                    {/* Items per page */}
                    <div className="hidden items-center gap-2 justify-center md:flex md:justify-start">
                        <Typography variant="small" className="text-gray-600 dark:text-gray-400 whitespace-nowrap">
                            Items per page:
                        </Typography>
                        <select
                            value={itemsPerPage}
                            onChange={(e) => {
                                setItemsPerPage(parseInt(e.target.value, 10));
                                setCurrentPage(0);
                            }}
                            className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 rounded-md px-2 py-1 text-sm w-18 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        >
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                            <option value={500}>500</option>
                            <option value={1000}>1000</option>
                        </select>
                    </div>

                    {/* Page info */}
                    <div className="text-center text-sm text-gray-600 dark:text-gray-400 md:text-left">
                        <span className="md:hidden">{totalElements} items</span>
                        <span className="hidden md:inline">Showing page {displayPage} of {totalPages} ({totalElements} total items)</span>
                    </div>

                    {/* Page navigation */}
                    <div className="flex justify-center items-center gap-2">
                        <IconButton size="sm" variant="outlined" onClick={handleFirstPage} disabled={isFirstPageDisabled}
                                    className="hidden text-cyan-800 dark:text-cyan-200 border-cyan-800 dark:border-cyan-200 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 md:inline-flex">
                            <ChevronDoubleLeftIcon strokeWidth={2} className="h-4 w-4" />
                        </IconButton>
                        <IconButton size="sm" variant="outlined" onClick={handlePrevPage} disabled={isFirstPageDisabled}
                                    className="text-cyan-800 dark:text-cyan-200 border-cyan-800 dark:border-cyan-200 hover:bg-cyan-50 dark:hover:bg-cyan-900/20">
                            <ChevronLeftIcon strokeWidth={2} className="h-4 w-4" />
                        </IconButton>
                        <Typography className="dark:text-gray-400 whitespace-nowrap">
                            <strong className="text-gray-900 dark:text-gray-300">{displayPage}</strong>
                            {" "}of{" "}
                            <strong className="text-gray-900 dark:text-gray-300">{totalPages}</strong>
                        </Typography>
                        <IconButton size="sm" variant="outlined" onClick={handleNextPage} disabled={isLastPageDisabled}
                                    className="text-cyan-800 dark:text-cyan-200 border-cyan-800 dark:border-cyan-200 hover:bg-cyan-50 dark:hover:bg-cyan-900/20">
                            <ChevronRightIcon strokeWidth={2} className="h-4 w-4" />
                        </IconButton>
                        <IconButton size="sm" variant="outlined" onClick={handleLastPage} disabled={isLastPageDisabled}
                                    className="hidden text-cyan-800 dark:text-cyan-200 border-cyan-800 dark:border-cyan-200 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 md:inline-flex">
                            <ChevronDoubleRightIcon strokeWidth={2} className="h-4 w-4" />
                        </IconButton>
                    </div>
                </div>

                {/* Search + Filter */}
                <div className="hidden items-center mt-5 w-full gap-2 md:flex md:w-max">
                    <div className="relative">
                        <Input type="search" variant="outlined" label="Title" placeholder="Search by title"
                               containerProps={{ className: "min-w-full sm:min-w-[288px]" }}
                               className="placeholder:text-blue-gray-300 dark:bg-dark-800"
                               color={isDark ? "white" : ""}
                               value={searchTerm}
                               onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }} />
                    </div>

                    <div className="relative" ref={filterRef}>
                        <button
                            onClick={() => setFilterOpen(p => !p)}
                            className="mv-primary-button gap-1">
                            <AdjustmentsHorizontalIcon className="w-5 h-5" />
                            {`(${activeFilterCount})`}
                        </button>

                        {filterOpen && (
                            <div className="absolute bottom-full mb-2 left-0 z-50 w-[300px] bg-white dark:bg-dark-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl p-3">

                                {/* Hide OOS */}
                                <div className="flex items-center gap-2 mb-3 cursor-pointer select-none" onClick={() => { setHideOutOfStock(p => !p); setCurrentPage(0); }}>
                                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${hideOutOfStock ? 'bg-cyan-700 border-cyan-700' : 'border-gray-400 dark:border-gray-500'}`}>
                                        {hideOutOfStock && <span className="text-white text-[10px] font-bold leading-none">✓</span>}
                                    </div>
                                    <span className="text-sm text-gray-700 dark:text-white">Hide OUT OF STOCK</span>
                                </div>

                                <div className="flex items-center gap-2 mb-3 cursor-pointer select-none" onClick={() => { setMissingMarketUrl(p => !p); setCurrentPage(0); }}>
                                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${missingMarketUrl ? 'bg-cyan-700 border-cyan-700' : 'border-gray-400 dark:border-gray-500'}`}>
                                        {missingMarketUrl && <span className="text-white text-[10px] font-bold leading-none">✓</span>}
                                    </div>
                                    <span className="text-sm text-gray-700 dark:text-white">Missing Market URL</span>
                                </div>

                                <div className="flex items-center gap-2 mb-3 cursor-pointer select-none" onClick={() => { setMissingEan(p => !p); setCurrentPage(0); }}>
                                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${missingEan ? 'bg-cyan-700 border-cyan-700' : 'border-gray-400 dark:border-gray-500'}`}>
                                        {missingEan && <span className="text-white text-[10px] font-bold leading-none">✓</span>}
                                    </div>
                                    <span className="text-sm text-gray-700 dark:text-white">Missing EAN</span>
                                </div>

                                <div className="mb-3">
                                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Match status</span>
                                    <div className="mt-1.5 flex flex-wrap gap-1">
                                        {MATCH_STATUSES.map(status => (
                                            <button
                                                type="button"
                                                key={status}
                                                onClick={() => {
                                                    setSelectedMatchStatuses(previous => previous.includes(status)
                                                        ? previous.filter(value => value !== status)
                                                        : [...previous, status]);
                                                    setCurrentPage(0);
                                                }}
                                                className={`rounded px-2 py-1 text-[10px] font-semibold ${
                                                    selectedMatchStatuses.includes(status)
                                                        ? 'bg-cyan-700 text-white'
                                                        : 'bg-gray-100 text-gray-600 dark:bg-dark-700 dark:text-gray-300'
                                                }`}
                                            >
                                                {MATCH_STATUS_LABELS[status]}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Checked</span>
                                    <select
                                        value={isChecked === null ? '' : isChecked.toString()}
                                        onChange={(e) => { setIsChecked(e.target.value === '' ? null : e.target.value === 'true'); setCurrentPage(0); }}
                                        className="w-full mt-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-900 px-2 py-1 text-xs text-gray-900 dark:text-gray-200 focus:outline-none focus:border-cyan-500"
                                    >
                                        <option value="">All</option>
                                        <option value="true">Checked</option>
                                        <option value="false">Not Checked</option>
                                    </select>
                                </div>

                                <hr className="mb-3 border-gray-200 dark:border-gray-600" />

                                {/* Platform */}
                                <div className="mb-3">
                                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Platform</span>
                                    <div className="flex gap-1 mt-1.5">
                                        {PLATFORMS.map(p => (
                                            <button key={p}
                                                    onClick={() => { setSelectedPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]); setCurrentPage(0); }}
                                                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${selectedPlatforms.includes(p) ? 'bg-cyan-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-dark-700 dark:text-gray-300'}`}>
                                                {p}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <hr className="mb-3 border-gray-200 dark:border-gray-600" />

                                {/* Range filters (client-side) */}
                                {[
                                    { label: 'QTY', min: qtyMin, max: qtyMax, setMin: setQtyMin, setMax: setQtyMax, step: '1' },
                                    { label: 'MY (RSD)', min: myMin, max: myMax, setMin: setMyMin, setMax: setMyMax, step: '1' },
                                    { label: 'WOB (GBP)', min: wobMin, max: wobMax, setMin: setWobMin, setMax: setWobMax, step: '0.01' },
                                    { label: 'MKT (USD)', min: mktMin, max: mktMax, setMin: setMktMin, setMax: setMktMax, step: '0.01' },
                                ].map(({ label, min, max, setMin, setMax, step }) => (
                                    <div key={label} className="flex items-center gap-2 mb-2">
                                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-[76px] shrink-0">{label}</span>
                                        <input type="number" step={step} placeholder="min" value={min}
                                               onChange={(e) => setMin(e.target.value)}
                                               className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-900 px-2 py-1 text-xs text-gray-900 dark:text-gray-200 focus:outline-none focus:border-cyan-500" />
                                        <span className="text-gray-300 text-xs shrink-0">—</span>
                                        <input type="number" step={step} placeholder="max" value={max}
                                               onChange={(e) => setMax(e.target.value)}
                                               className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-900 px-2 py-1 text-xs text-gray-900 dark:text-gray-200 focus:outline-none focus:border-cyan-500" />
                                    </div>
                                ))}

                                <hr className="mt-2 mb-2 border-gray-200 dark:border-gray-600" />

                                <button onClick={() => {
                                    setHideOutOfStock(false); setSelectedPlatforms([]);
                                    setMyMin(''); setMyMax(''); setWobMin(''); setWobMax('');
                                    setQtyMin(''); setQtyMax(''); setMktMin(''); setMktMax(''); setMissingMarketUrl(false); setIsChecked(null);
                                    setSelectedMatchStatuses([]); setMissingEan(false);
                                    setCurrentPage(0);
                                }} className="w-full text-center text-xs text-cyan-600 dark:text-cyan-400 hover:text-cyan-800 py-1">
                                    Reset all filters
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <Dialog
                    size="lg"
                    open={isMatchDialogOpen}
                    handler={closeMatchDialog}
                    className="max-h-[92vh] overflow-hidden p-2 dark:bg-dark-800"
                >
                    <DialogHeader className="relative block border-b border-gray-100 dark:border-gray-700">
                        <Typography variant="h4" color="blue-gray" className="pr-10 dark:text-gray-200">
                            PriceCharting match review
                        </Typography>
                        <Typography className="mt-1 pr-10 text-sm font-normal text-gray-600 dark:text-gray-400">
                            {matchDialogItem?.title} · {matchDialogItem?.platform}
                            {matchDialogItem?.ean ? ` · EAN ${matchDialogItem.ean}` : ' · EAN unavailable'}
                        </Typography>
                        <IconButton
                            size="sm"
                            variant="text"
                            className="!absolute right-3 top-3"
                            onClick={closeMatchDialog}
                            disabled={matchActionLoading}
                        >
                            <XMarkIcon className="h-4 w-4 stroke-2" />
                        </IconButton>
                    </DialogHeader>
                    <DialogBody className="max-h-[66vh] overflow-y-auto">
                        {!matchDetails && !matchActionError ? (
                            <div className="py-12 text-center text-gray-500">Loading match evidence…</div>
                        ) : (
                            <div className="space-y-4">
                                {matchDetails && (
                                    <div className="grid gap-2 sm:grid-cols-4">
                                        <div className="rounded-lg bg-gray-50 p-3 dark:bg-dark-900">
                                            <p className="text-[10px] font-semibold uppercase text-gray-400">Status</p>
                                            <div className="mt-1"><span className={matchStatusClass(matchDetails.status)}>
                                                {MATCH_STATUS_LABELS[matchDetails.status] || matchDetails.status}
                                            </span></div>
                                        </div>
                                        <div className="rounded-lg bg-gray-50 p-3 dark:bg-dark-900">
                                            <p className="text-[10px] font-semibold uppercase text-gray-400">Method</p>
                                            <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-gray-200">
                                                {matchDetails.method?.replace(/_/g, ' ') || '—'}
                                            </p>
                                        </div>
                                        <div className="rounded-lg bg-gray-50 p-3 dark:bg-dark-900">
                                            <p className="text-[10px] font-semibold uppercase text-gray-400">Confidence</p>
                                            <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-gray-200">
                                                {Number.isFinite(matchDetails.confidence)
                                                    ? `${Math.round(matchDetails.confidence * 100)}%`
                                                    : '—'}
                                            </p>
                                        </div>
                                        <div className="rounded-lg bg-gray-50 p-3 dark:bg-dark-900">
                                            <p className="text-[10px] font-semibold uppercase text-gray-400">History</p>
                                            <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-gray-200">
                                                {matchHistory?.availability?.length || 0} stock · {matchHistory?.marketPrices?.length || 0} prices
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {matchDetails?.reason && (
                                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
                                        {matchDetails.reason}
                                    </div>
                                )}

                                {matchActionError && (
                                    <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-300">
                                        {matchActionError}
                                    </div>
                                )}

                                {matchHistory?.listings?.length > 0 && (
                                    <div className="rounded-xl border border-violet-100 bg-violet-50/40 p-3 dark:border-violet-900/60 dark:bg-violet-950/10">
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                WoB listings for this product
                                            </p>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                {matchHistory.listings.length} retained
                                            </span>
                                        </div>
                                        <div className="mt-2 space-y-2">
                                            {matchHistory.listings.map(listing => (
                                                <div
                                                    key={listing.listingId}
                                                    className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 ${
                                                        listing.active
                                                            ? 'border-violet-300 bg-white dark:border-violet-700 dark:bg-dark-900'
                                                            : 'border-gray-200 bg-gray-50/70 dark:border-gray-700 dark:bg-dark-900/50'
                                                    }`}
                                                >
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <p className="truncate text-sm font-semibold text-gray-800 dark:text-gray-200">
                                                                {listing.title}
                                                            </p>
                                                            {listing.active && (
                                                                <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                                                                    Active link
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                                            {listing.status?.replace(/_/g, ' ')} · ×{listing.quantity ?? 0}
                                                            {listing.lastSeenAt
                                                                ? ` · seen ${new Date(listing.lastSeenAt).toLocaleDateString('en-GB')}`
                                                                : ''}
                                                        </p>
                                                    </div>
                                                    {listing.url && (
                                                        <a
                                                            href={listing.url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="shrink-0 text-cyan-700 hover:text-cyan-900 dark:text-cyan-400"
                                                            title="Open this WoB listing"
                                                        >
                                                            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                                        </a>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="rounded-xl border border-cyan-100 bg-cyan-50/40 p-3 dark:border-cyan-900/60 dark:bg-cyan-950/10">
                                    <div className="flex flex-wrap items-end justify-between gap-2">
                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                Search the local PAL {matchDialogItem?.platform} catalog
                                            </p>
                                            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                                Search by title or exact PriceCharting product ID.
                                            </p>
                                        </div>
                                        {catalogSearchTerm.trim().length >= 2 && !catalogSearchLoading && (
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                {catalogSearchTotal} result{catalogSearchTotal === 1 ? '' : 's'}
                                            </span>
                                        )}
                                    </div>
                                    <input
                                        type="search"
                                        value={catalogSearchTerm}
                                        onChange={(event) => setCatalogSearchTerm(event.target.value)}
                                        placeholder={`Example: ${matchDialogItem?.title || 'Dying Light'}`}
                                        autoComplete="off"
                                        className="mt-3 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition-colors focus:border-cyan-600 dark:border-gray-700 dark:bg-dark-900 dark:text-gray-100"
                                    />

                                    {catalogSearchLoading && (
                                        <div className="py-5 text-center text-sm text-gray-500">
                                            Searching the local PAL catalog…
                                        </div>
                                    )}
                                    {catalogSearchError && (
                                        <div className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-300">
                                            {catalogSearchError}
                                        </div>
                                    )}
                                    {!catalogSearchLoading && catalogSearchTerm.trim().length >= 2 && (
                                        <div className="mt-3 space-y-2">
                                            {catalogSearchResults.map(product => (
                                                <CatalogProductCard
                                                    key={`search-${product.priceChartingProductId}`}
                                                    product={product}
                                                />
                                            ))}
                                            {catalogSearchResults.length === 0 && !catalogSearchError && (
                                                <div className="rounded-lg border border-dashed border-gray-300 px-3 py-5 text-center text-sm text-gray-500 dark:border-gray-700">
                                                    No PAL {matchDialogItem?.platform} title matches this search.
                                                </div>
                                            )}
                                            {catalogSearchTotal > catalogSearchResults.length && (
                                                <p className="text-center text-xs text-gray-500 dark:text-gray-400">
                                                    Showing the first {catalogSearchResults.length} results. Refine the title to narrow the list.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                        Automatic PAL candidates on the same platform
                                    </p>
                                    <div className="space-y-2">
                                        {(matchDetails?.candidates || []).map(candidate => (
                                            <CatalogProductCard
                                                key={`candidate-${candidate.priceChartingProductId}`}
                                                product={candidate}
                                                showScore
                                            />
                                        ))}
                                        {matchDetails && matchDetails.candidates?.length === 0 && (
                                            <div className="rounded-lg border border-dashed border-gray-300 px-3 py-6 text-center text-sm text-gray-500 dark:border-gray-700">
                                                No automatic candidate is available. Use the local PAL catalog search above.
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <FieldLabel>Exclusion reason (optional)</FieldLabel>
                                    <Input
                                        value={exclusionReason}
                                        onChange={(event) => setExclusionReason(event.target.value)}
                                        placeholder="German edition, NTSC import, no UK PAL release…"
                                        color={isDark ? "white" : "gray"}
                                        className={inputCls}
                                        labelProps={labelHidden}
                                    />
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        If empty, Arcado records this as a manual exclusion.
                                    </p>
                                </div>
                            </div>
                        )}
                    </DialogBody>
                    <DialogFooter className="flex flex-wrap justify-between gap-2 border-t border-gray-100 dark:border-gray-700">
                        <Button
                            type="button"
                            className="mv-danger-button"
                            disabled={matchActionLoading}
                            onClick={resetMatch}
                        >
                            <ArrowPathIcon className="h-4 w-4 stroke-2" />
                            Reset mapping
                        </Button>
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                className="mv-secondary-button"
                                disabled={matchActionLoading}
                                onClick={excludeMatch}
                            >
                                <NoSymbolIcon className="h-4 w-4 stroke-2" />
                                Exclude
                            </Button>
                            <Button
                                type="button"
                                className="mv-primary-button"
                                disabled={matchActionLoading || !selectedCandidateId}
                                onClick={confirmMatch}
                            >
                                <CheckCircleIcon className="h-4 w-4 stroke-2" />
                                Confirm PAL match
                            </Button>
                        </div>
                    </DialogFooter>
                </Dialog>

                {/* ADD DIALOG */}
                <Dialog size="md" open={isAddDialogOpen} handler={toggleAddDialog} className="p-4 mt-10 mb-10 dark:bg-dark-800">
                    <DialogHeader className="relative m-0 block">
                        <Typography variant="h4" color="blue-gray" className="dark:text-gray-200">Add to Watchlist</Typography>
                        <Typography className="mt-1 font-normal text-gray-600 dark:text-gray-400">Add a new item to track.</Typography>
                        <IconButton size="sm" variant="text" className="!absolute right-3.5 top-3.5" onClick={toggleAddDialog}>
                            <XMarkIcon className="h-4 w-4 stroke-2" />
                        </IconButton>
                    </DialogHeader>
                    <form onSubmit={handleAdd}>
                        <DialogBody className="pb-6 overflow-y-auto max-h-[65vh]">
                            <DialogFields data={newItemData} onChange={handleNewInput} isNew />
                        </DialogBody>
                        <DialogFooter>
                            <Button type="submit" className="mv-primary-button ml-auto">
                                <PlusCircleIcon className="h-4 w-4" />
                                Add
                            </Button>
                        </DialogFooter>
                    </form>
                </Dialog>

                {/* EDIT DIALOG */}
                <Dialog size="md" open={isEditDialogOpen} handler={() => toggleEditDialog(null)} className="p-4 mt-10 mb-10 dark:bg-dark-800">
                    <DialogHeader className="relative m-0 block">
                        <Typography variant="h4" color="blue-gray" className="dark:text-gray-200">Edit Watchlist Item</Typography>
                        <Typography className="mt-1 font-normal text-gray-600 dark:text-gray-400">Update item details.</Typography>
                        <IconButton size="sm" variant="text" className="!absolute right-3.5 top-3.5" onClick={() => toggleEditDialog(null)}>
                            <XMarkIcon className="h-4 w-4 stroke-2" />
                        </IconButton>
                    </DialogHeader>
                    <form onSubmit={handleUpdate}>
                        <DialogBody className="pb-6 overflow-y-auto max-h-[65vh]">
                            <DialogFields data={itemToEdit} onChange={handleEditInput} />
                        </DialogBody>
                        <DialogFooter className="flex items-center justify-between gap-2">
                            <Button onClick={startDeleteCountdown}
                                    className={deleteTimer !== null ? "mv-warning-button" : "mv-danger-button"}>
                                <TrashIcon className="h-4 w-4" />
                                {deleteTimer !== null ? `Abandon (${deleteTimer})` : "Delete"}
                            </Button>
                            <Button type="submit" className="mv-primary-button">
                                <CheckCircleIcon className="h-4 w-4" />
                                Save
                            </Button>
                        </DialogFooter>
                    </form>
                </Dialog>

            </CardBody>
        </Card>
    );
}
